import { create } from 'zustand';
import { User } from 'firebase/auth';
import { authService, UserProfile } from '../services/authService';

interface AuthState {
    currentUser: User | null;
    userProfile: UserProfile | null;
    loading: boolean;
    initialized: boolean;
    
    // Actions
    setAuth: (user: User | null, profile: UserProfile | null) => void;
    setLoading: (loading: boolean) => void;
    setProfile: (profile: UserProfile | null) => void;
    
    // Auth Methods
    signOut: () => Promise<void>;
    updateProfile: (updates: Partial<UserProfile>) => Promise<void>;
    refreshProfile: () => Promise<void>;
    completeOnboarding: (data: Partial<UserProfile>) => Promise<void>;
    signIn: (email: string, password: string) => Promise<void>;
    signUp: (email: string, password: string, displayName: string) => Promise<UserProfile>;
    signInWithGoogle: () => Promise<void>;
    sendPasswordResetEmail: (email: string) => Promise<void>;
}

export const useAuthStore = create<AuthState>((set, get) => ({
    currentUser: null,
    userProfile: null,
    loading: true,
    initialized: false,

    setAuth: (user, profile) => set({ 
        currentUser: user, 
        userProfile: profile, 
        loading: false,
        initialized: true 
    }),
    
    setLoading: (loading) => set({ loading }),
    
    setProfile: (profile) => set({ userProfile: profile }),

    signOut: async () => {
        await authService.signOut();
        set({ currentUser: null, userProfile: null });
    },

    updateProfile: async (updates: Partial<UserProfile>) => {
        const { currentUser } = get();
        if (!currentUser) return;
        const { userService } = await import('../services/userService');
        await userService.updateUserProfile(currentUser.uid, updates);
        await get().refreshProfile();
    },

    refreshProfile: async () => {
        const { currentUser } = get();
        if (!currentUser) return;
        const profile = await authService.getUserProfile(currentUser.uid);
        set({ userProfile: profile });
    },

    completeOnboarding: async (data: Partial<UserProfile>) => {
        const { updateProfile } = get();
        await updateProfile({
            ...data,
            onboardingComplete: true
        });
    },

    signIn: async (email: string, password: string) => {
        const profile = await authService.signIn(email, password);
        set({ userProfile: profile });
    },

    signUp: async (email: string, password: string, displayName: string) => {
        const profile = await authService.signUp(email, password, displayName);
        set({ userProfile: profile });
        return profile;
    },

    signInWithGoogle: async () => {
        const profile = await authService.signInWithGoogle();
        set({ userProfile: profile });
    },

    sendPasswordResetEmail: async (email: string) => {
        await authService.sendPasswordResetEmail(email);
    }
}));

// Initialize listener
authService.onAuthChange(async (user) => {
    if (user) {
        useAuthStore.getState().setLoading(true);
        const profile = await authService.getUserProfile(user.uid);
        useAuthStore.getState().setAuth(user, profile);
    } else {
        useAuthStore.getState().setAuth(null, null);
    }
});
